import 'react-tooltip/dist/react-tooltip.css';


import styles from './Page3_OfferImpact.module.scss';
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Info, CheckCircle, ChevronLeft, ChevronRight, X, Grid } from 'react-feather';
import { Tooltip } from 'react-tooltip';

const TOTAL_STEPS = 5;
const CURRENT_STEP = 3;
const RED = '#c0392b';

// ─── Responsive hook ──────────────────────────────────────────────────────────
const useWindowWidth = () => {
  const [width, setWidth] = React.useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  React.useEffect(() => {
    const handle = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return width;
};

// ─── Normalizers ──────────────────────────────────────────────────────────────
const normalizeGoal = g => {
  if (!g) return 'No Goal';
  const s = String(g).toLowerCase();
  if (s.includes('more customers') || s.includes('attract')) return 'New Customers';
  if (s.includes('sell')) return 'Sell More';
  if (s.includes('repeat')) return 'Repeat Business';
  if (s.includes('slow') || s.includes('off-peak')) return 'Slow Periods';
  if (s.includes('special')) return 'Special Promotion';
  return 'No Goal';
};

const normalizeScope = sc => {
  if (!sc) return 'Individual Item';
  const s = String(sc).toLowerCase();
  if (s.includes('individual')) return 'Individual Item';
  if (s.includes('line') || s.includes('category')) return 'Product Line';
  if (s.includes('store')) return 'Storewide Promotion';
  return 'Individual Item';
};

// ─── Constants ────────────────────────────────────────────────────────────────
const OFFER_IMPACTS = [
  {
    key: 'steady_growth',
    title: 'Steady Growth',
    subtitle: 'Gentle Impact',
    implied: 'Attracts consistent interest, maintains margins.',
    tooltip: "Ideal for consistent, sustainable traffic and maintaining healthy profit margins. Suggested 'Available per user' setting: Unlimited (Basic) or Multiple Times (Premium).",
  },
  {
    key: 'quick_boost',
    title: 'Quick Boost',
    subtitle: 'Noticeable Impact',
    implied: 'Drives a clear increase in sales/traffic over a short period.',
    tooltip: "Best for a short, clear spike in sales or traffic. Suggested 'Available per user' setting: One Time or low Custom (Premium).",
  },
  {
    key: 'maximum_impact',
    title: 'Maximum Impact',
    subtitle: 'Aggressive Demand',
    implied: 'Generates significant, rapid demand at lower margins.',
    tooltip: "For rapid movement or high-volume acquisition. Aggressive discount values and short, urgent validity windows. Suggested 'Available per user' setting: One Time (Premium).",
  },
];

const SPEED_MAP = { steady_growth: 'Steady', quick_boost: 'Quick', maximum_impact: 'Max' };
const SLIDER_POS = { steady_growth: 0, quick_boost: 50, maximum_impact: 100 };

// ─── Color interpolation ──────────────────────────────────────────────────────
function lerpColor(pct) {
  const stops = [
    { p: 0,   r: 0xFF, g: 0xD2, b: 0x10 },
    { p: 50,  r: 0xFF, g: 0x88, b: 0x00 },
    { p: 100, r: 0xE1, g: 0x00, b: 0x00 },
  ];
  let lo = stops[0], hi = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i++) {
    if (pct >= stops[i].p && pct <= stops[i + 1].p) { lo = stops[i]; hi = stops[i + 1]; break; }
  }
  const t = lo.p === hi.p ? 0 : (pct - lo.p) / (hi.p - lo.p);
  return `rgb(${Math.round(lo.r + t * (hi.r - lo.r))},${Math.round(lo.g + t * (hi.g - lo.g))},${Math.round(lo.b + t * (hi.b - lo.b))})`;
}

// ─── Icon ─────────────────────────────────────────────────────────────────────
function ImpactIcon({ active }) {
  return (
    <div style={{
      width: 44, height: 44, borderRadius: 10, flexShrink: 0,
      background: active ? 'rgba(255,255,255,0.2)' : 'rgba(192,57,43,0.10)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Grid size={22} color={active ? '#fff' : RED} />
    </div>
  );
}

// ─── Slider ───────────────────────────────────────────────────────────────────
function ImpactSlider({ selected, onSelect }) {
  const trackRef = useRef(null);
  const isDragging = useRef(false);
  const snapPos = selected ? (SLIDER_POS[selected] ?? 0) : 0;
  const [livePos, setLivePos] = useState(snapPos);

  useEffect(() => {
    if (!isDragging.current) setLivePos(snapPos);
  }, [snapPos]);

  const getRaw = useCallback((clientX) => {
    const el = trackRef.current;
    if (!el) return 0;
    const { left, width } = el.getBoundingClientRect();
    return Math.min(100, Math.max(0, ((clientX - left - 22) / (width - 44)) * 100));
  }, []);

  const snapToKey = (pct) =>
    [...OFFER_IMPACTS].sort((a, b) =>
      Math.abs(SLIDER_POS[a.key] - pct) - Math.abs(SLIDER_POS[b.key] - pct)
    )[0].key;

  const onPointerDown = (e) => {
    e.preventDefault();
    isDragging.current = true;
    trackRef.current?.setPointerCapture(e.pointerId);
    setLivePos(getRaw(e.clientX));
  };

  const onPointerMove = (e) => {
    if (!isDragging.current) return;
    setLivePos(getRaw(e.clientX));
  };

  const onPointerUp = (e) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const raw = getRaw(e.clientX);
    const key = snapToKey(raw);
    onSelect(key);
    setLivePos(SLIDER_POS[key]);
  };

  const pos = livePos;
  const color = lerpColor(pos);
  const snapping = !isDragging.current;

  return (
    <div
      ref={trackRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      style={{
        position: 'relative',
        height: 44,
        borderRadius: 999,
        background: 'rgba(200,180,160,0.18)',
        border: '1.5px solid rgba(200,180,160,0.3)',
        cursor: 'pointer',
        userSelect: 'none',
        touchAction: 'none',
        boxSizing: 'border-box',
      }}
    >
      {/* Colored fill */}
      <div style={{
        position: 'absolute',
        left: 0, top: 0, bottom: 0,
        width: `calc(22px + ${pos / 100} * (100% - 44px))`,
        minWidth: 44,
        borderRadius: 999,
        background: `linear-gradient(90deg, #FFD210 0%, ${color} 100%)`,
        transition: snapping ? 'width 0.3s cubic-bezier(.4,0,.2,1)' : 'none',
        pointerEvents: 'none',
        zIndex: 1,
      }} />

      {/* Thumb */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: `calc(${pos / 100} * (100% - 44px))`,
        transform: 'translateY(-50%)',
        width: 44, height: 44,
        borderRadius: '50%',
        background: '#fff',
        border: `3.5px solid ${color}`,
        boxShadow: '0 2px 14px rgba(0,0,0,0.22)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: snapping
          ? 'left 0.3s cubic-bezier(.4,0,.2,1), border-color 0.15s'
          : 'border-color 0.05s',
        zIndex: 3,
        pointerEvents: 'none',
        boxSizing: 'border-box',
      }}>
        <div style={{
          width: 14, height: 14,
          borderRadius: '50%',
          border: `3px solid ${color}`,
          background: '#fff',
          transition: 'border-color 0.05s',
        }} />
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────
export default function Page3_OfferImpact({
  goal, scope,
  itemName = '', itemPrice = '', itemDescription = '',
  selectedImpact,
  onNext, onBack, onClose,
}) {
  const [selected, setSelected] = useState(selectedImpact || '');
  const pct = useMemo(() => Math.round((CURRENT_STEP / TOTAL_STEPS) * 100), []);

  const width = useWindowWidth();
  const isMobile = width < 641;
  const isTablet = width < 821;
  const pagePaddingH = isMobile ? 14 : isTablet ? 24 : 40;

  const handleNext = () => {
    if (!selected) return;
    const speed = SPEED_MAP[selected];
    try {
      localStorage.setItem('cbv2_offerImpactKey', selected);
      localStorage.setItem('cbv2_speed', speed);
      localStorage.setItem('cbv2_itemName', itemName || '');
      localStorage.setItem('cbv2_itemPrice', String(itemPrice ?? ''));
      localStorage.setItem('cbv2_itemDesc', itemDescription || '');
    } catch {}
    onNext({
      offerImpact: selected, speed,
      itemName: itemName?.trim(),
      itemPrice: itemPrice === '' ? '' : Number(itemPrice),
      itemDescription: itemDescription?.trim(),
      heroImagePreview: null,
    });
  };

  // Grid columns: 3 on desktop, 1 on mobile/tablet (cards stack vertically)
  const gridCols = isMobile || isTablet ? '1fr' : 'repeat(3,1fr)';

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(150deg, #f7e8e8 0%, #eddcdc 50%, #e6cdcd 100%)',
      fontFamily: "'Inter','Segoe UI',sans-serif",
      padding: '0 0 40px',
      boxSizing: 'border-box',
    }}>

      {/* ── Top bar ── */}
      <div style={{ padding: `20px ${pagePaddingH}px 0`, position: 'relative' }}>

        {/* Close button */}
        {!isMobile && (
          <button type="button" onClick={onClose} style={{
            position: 'absolute', top: 20, right: pagePaddingH,
            width: 36, height: 36, background: '#fff',
            border: '1.5px solid #e0d5d5', borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: '#888',
          }}><X size={15} /></button>
        )}

        {/* Progress */}
        <div style={{ paddingRight: isMobile ? 0 : 56, marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
              const n = i + 1, isActive = n === CURRENT_STEP, isDone = n < CURRENT_STEP;
              return (
                <React.Fragment key={n}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                    background: isActive || isDone ? RED : 'transparent',
                    border: isActive || isDone ? 'none' : '2px solid #ccc',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 12, fontWeight: 700,
                    color: isActive || isDone ? '#fff' : '#bbb',
                  }}>{n}</div>
                  {i < TOTAL_STEPS - 1 && (
                    <div style={{ flex: 1, height: 2, background: isDone ? RED : '#ddd' }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <h2 style={{
          margin: '0 0 8px',
          fontSize: isMobile ? 20 : isTablet ? 22 : 24,
          fontWeight: 800, color: '#1a1a1a', letterSpacing: '-0.3px',
          maxWidth: 640,
        }}>
          What kind of result are you looking for with this offer
        </h2>
        <p style={{ margin: '0 0 28px', fontSize: 13, color: '#888', lineHeight: 1.65, maxWidth: 400 }}>
          This helps us tailor the discount, urgency, and overall offer strategy to best achieve your business goals. You can always fine-tune these details later.
        </p>
      </div>

      {/* ── White panel ── */}
      <div style={{
        margin: `0 ${pagePaddingH}px`,
        background: '#fff',
        borderRadius: 20,
        padding: isMobile ? '20px 16px 20px' : '32px 28px 28px',
        boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
      }}>

        {/* Impact cards */}
        <div
          role="radiogroup"
          aria-label="Offer impact"
          style={{
            display: 'grid',
            gridTemplateColumns: gridCols,
            gap: isMobile ? 12 : 16,
            marginBottom: 28,
          }}
        >
          {OFFER_IMPACTS.map(impact => {
            const isSelected = selected === impact.key;
            const tipId = `tip-${impact.key}`;

            return (
              <button
                key={impact.key}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => setSelected(isSelected ? '' : impact.key)}
                style={{
                  position: 'relative',
                  background: isSelected ? RED : '#fff',
                  border: isSelected ? 'none' : '1.5px solid #e5e7eb',
                  borderRadius: 14,
                  padding: isMobile ? '16px 14px' : '20px 18px',
                  cursor: 'pointer', textAlign: 'left',
                  display: 'flex',
                  flexDirection: (isMobile || isTablet) ? 'row' : 'column',
                  alignItems: (isMobile || isTablet) ? 'center' : 'flex-start',
                  gap: (isMobile || isTablet) ? 0 : 0,
                  boxShadow: isSelected ? '0 4px 20px rgba(192,57,43,0.25)' : 'none',
                  transition: 'background .15s, box-shadow .15s',
                  minHeight: (isMobile || isTablet) ? 'auto' : 180,
                }}
              >
                {/* Info button */}
                <button
                  type="button"
                  onClick={e => e.stopPropagation()}
                  data-tooltip-id={tipId}
                  data-tooltip-content={impact.tooltip}
                  style={{
                    position: 'absolute', top: 10, right: 10,
                    background: 'none',
                    border: isSelected ? '1.5px solid rgba(255,255,255,0.6)' : `1.5px solid ${RED}`,
                    borderRadius: '50%', width: 24, height: 24,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: isSelected ? '#fff' : RED, padding: 0,
                  }}
                >
                  <Info size={12} />
                </button>
                <Tooltip id={tipId} place="top" style={{ fontSize: 12, maxWidth: 260 }} />

                {/* Icon + title row */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: isMobile || isTablet ? 0 : 20,
                  flexShrink: 0,
                  flex: isMobile || isTablet ? 1 : 'unset',
                }}>
                  <ImpactIcon active={isSelected} />
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: 15, fontWeight: 800,
                      color: isSelected ? '#fff' : '#1a1a1a',
                      lineHeight: 1.25,
                    }}>
                      {impact.title}
                    </div>
                    <div style={{
                      fontSize: 12,
                      color: isSelected ? 'rgba(255,255,255,0.75)' : '#9ca3af',
                      marginTop: 2,
                    }}>
                      {impact.subtitle}
                    </div>
                    {/* On mobile/tablet: show description inline as a small muted line */}
                    {(isMobile || isTablet) && (
                      <div style={{
                        fontSize: 11,
                        color: isSelected ? 'rgba(255,255,255,0.7)' : '#b0b7c3',
                        marginTop: 4,
                        lineHeight: 1.4,
                        paddingRight: 28,
                      }}>
                        {impact.implied}
                      </div>
                    )}
                  </div>
                </div>

                {/* Description — desktop only, shown below icon block */}
                {!isMobile && !isTablet && (
                  <div style={{
                    fontSize: 13,
                    color: isSelected ? 'rgba(255,255,255,0.85)' : '#6b7280',
                    lineHeight: 1.5,
                    marginTop: 'auto',
                  }}>
                    {impact.implied}
                  </div>
                )}

                {isSelected && (
                  <CheckCircle
                    size={18}
                    color="#fff"
                    style={{ position: 'absolute', bottom: 14, right: 14 }}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Slider */}
        <ImpactSlider selected={selected} onSelect={setSelected} />
      </div>

      {/* ── Nav buttons ── */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: `24px ${pagePaddingH}px 0`,
        gap: 12,
        flexWrap: 'wrap',
      }}>
        <button type="button" onClick={onBack} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: isMobile ? '9px 16px' : '10px 20px', borderRadius: 8,
          border: `1.5px solid ${RED}`, background: '#fff',
          color: RED, fontSize: 14, fontWeight: 600, cursor: 'pointer',
        }}>
          <ChevronLeft size={15} /> Back
        </button>
        <button type="button" onClick={handleNext} disabled={!selected} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: isMobile ? '9px 20px' : '10px 24px', borderRadius: 8,
          border: 'none',
          background: !selected ? '#e5e7eb' : RED,
          color: !selected ? '#9ca3af' : '#fff',
          fontSize: 14, fontWeight: 700,
          cursor: !selected ? 'not-allowed' : 'pointer',
        }}>
          Next <ChevronRight size={15} />
        </button>
      </div>

    </div>
  );
}